import { NavLink } from 'react-router-dom'
import { Sun, Moon, Wallet, ChevronDown, Loader2 } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { useChainId, useSwitchChain, useAccount } from 'wagmi'
import { useThemeStore } from '@/store/theme'
import { ConnectButton } from '@rainbow-me/rainbowkit'
import { SUPPORTED_CHAINS } from '@/config/chains'

const CHAIN_LOGOS: Record<number, string> = {
  97: '/chain-97.svg',
  5042002: '/chain-5042002.svg',
}

function ChainLogo({ chainId, size = 18 }: { chainId: number; size?: number }) {
  const src = CHAIN_LOGOS[chainId]
  if (!src) return null
  return (
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      style={{ borderRadius: '50%', flexShrink: 0 }}
    />
  )
}

function ChainSelector() {
  const chainId = useChainId()
  const { switchChainAsync } = useSwitchChain()
  const { isConnected, connector } = useAccount()
  const [open, setOpen] = useState(false)
  const [switching, setSwitching] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const currentChain = SUPPORTED_CHAINS.find(c => c.id === chainId) || SUPPORTED_CHAINS[0]

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleSwitchChain = async (targetChainId: number) => {
    if (targetChainId === chainId) { setOpen(false); return }
    setSwitching(true)
    try {
      if (isConnected && connector) {
        // Wallet connected — directly request chain switch via provider
        // This guarantees the wallet popup appears for user confirmation
        const provider = await connector.getProvider()
        const hexChainId = `0x${targetChainId.toString(16)}`
        try {
          await (provider as any).request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: hexChainId }],
          })
        } catch (err: any) {
          // 4902 = chain not in wallet, need to add it first
          if (err?.code === 4902) {
            const chain = SUPPORTED_CHAINS.find(c => c.id === targetChainId)
            if (chain) {
              await (provider as any).request({
                method: 'wallet_addEthereumChain',
                params: [{
                  chainId: hexChainId,
                  chainName: chain.name,
                  nativeCurrency: chain.nativeCurrency,
                  rpcUrls: chain.rpcUrls.default.http,
                  blockExplorerUrls: chain.blockExplorers?.default
                    ? [chain.blockExplorers.default.url] : [],
                }],
              })
            } else { throw err }
          } else { throw err }
        }
      } else {
        // Not connected — just update wagmi config chain
        await switchChainAsync({ chainId: targetChainId })
      }
      setOpen(false)
    } catch (e: any) {
      // User rejected or wallet error — stay on current chain
      console.warn('Chain switch failed:', e?.message || e)
    } finally {
      setSwitching(false)
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        disabled={switching}
        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm transition-colors hover:bg-[var(--bg-secondary)]"
        style={{ color: 'var(--text-secondary)' }}
      >
        {switching ? <Loader2 size={16} className="animate-spin" /> : <ChainLogo chainId={currentChain.id} />}
        {currentChain.name}
        <ChevronDown size={14} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 min-w-[190px] rounded-xl border py-1 shadow-lg z-50"
          style={{
            backgroundColor: 'var(--bg-secondary)',
            borderColor: 'var(--border)',
          }}
        >
          {SUPPORTED_CHAINS.map(chain => (
            <button
              key={chain.id}
              disabled={switching}
              onClick={() => handleSwitchChain(chain.id)}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors hover:bg-[var(--bg-tertiary)]"
              style={{
                color: chain.id === chainId ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontWeight: chain.id === chainId ? 600 : 400,
                opacity: switching ? 0.5 : 1,
              }}
            >
              <ChainLogo chainId={chain.id} size={20} />
              <span className="flex-1 text-left">{chain.name}</span>
              {chain.id === chainId && (
                <span style={{ color: 'var(--success)', fontSize: 12 }}>✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function Header() {
  const { isDark, toggle } = useThemeStore()

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b backdrop-blur-md"
      style={{ borderColor: 'var(--border)', backgroundColor: 'color-mix(in srgb, var(--bg-primary) 80%, transparent)' }}>
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-8">
          <NavLink to="/" className="flex items-center gap-2 font-bold text-xl" style={{ color: 'var(--text-primary)' }}>
            <img src="/axon-logo.svg" alt="AxonSwap" className="w-8 h-8" style={{ filter: isDark ? 'invert(1)' : 'none' }} />
            AxonSwap
          </NavLink>

          {/* Nav Links */}
          <nav className="hidden md:flex items-center gap-1">
            {[
              { to: '/swap', label: 'Swap' },
              { to: '/pool', label: 'Pool' },
              { to: '/tokens', label: 'Tokens' },
            ].map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[var(--bg-tertiary)]'
                      : 'hover:bg-[var(--bg-secondary)]'
                  }`
                }
                style={({ isActive }) => ({
                  color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                })}
              >
                {label}
              </NavLink>
            ))}
          </nav>
        </div>

        {/* Right Side */}
        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            className="p-2 rounded-xl transition-colors hover:bg-[var(--bg-secondary)]"
            style={{ color: 'var(--text-secondary)' }}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          <ChainSelector />

          <ConnectButton.Custom>
            {({ account, chain, openAccountModal, openChainModal, openConnectModal, mounted }) => {
              const connected = mounted && account && chain
              return (
                <div {...(!mounted && { 'aria-hidden': true, style: { opacity: 0, pointerEvents: 'none' as const, userSelect: 'none' as const } })}>
                  {(() => {
                    if (!connected) {
                      return (
                        <button
                          onClick={openConnectModal}
                          className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all"
                          style={{
                            backgroundColor: 'var(--accent)',
                            color: 'var(--bg-primary)',
                          }}
                        >
                          <Wallet size={16} />
                          Connect Wallet
                        </button>
                      )
                    }
                    if (chain.unsupported) {
                      return (
                        <button onClick={openChainModal} className="px-4 py-2.5 rounded-xl font-medium text-sm bg-[var(--error)] text-white">
                          Wrong Network
                        </button>
                      )
                    }
                    return (
                      <button
                        onClick={openAccountModal}
                        className="px-4 py-2.5 rounded-xl font-medium text-sm transition-colors"
                        style={{
                          backgroundColor: 'var(--bg-tertiary)',
                          color: 'var(--text-primary)',
                        }}
                      >
                        {account.displayName}
                      </button>
                    )
                  })()}
                </div>
              )
            }}
          </ConnectButton.Custom>
        </div>
      </div>
    </header>
  )
}
